import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useMemo} from 'react';
import type {
  FormDefinitionSimpleResponse} from 'src/api/ProactApiClient';
import {
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {
  ActiveInactivePills,
  YesNoPills,
} from 'src/shared/constants/PillConstants';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {
  ActiveStateDataOptions,
  YesNoStateDataOptions,
} from 'src/shared/constants/columnFilterData';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useGetFormDefinitionListV2Query} from 'src/redux/queries/formDefinition';
import {useHasModulePermission} from 'src/shared/auth/useHasModulePermission';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const ListFormBuilderDefinitions: React.FC = () => {
  const navigate = useAppNavigate();

  const {hasPermission: hasCreatePermission} = useHasModulePermission({
    module: ModuleEnum.Forms,
    operation: PermissionOperationEnum.Create,
  });

  const columns = useMemo<MRT_ColumnDef<FormDefinitionSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'uniqueIdentifier',
        header: 'Form Identifier',
      },
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'description',
        header: 'Description',
        Cell: ({cell}) => (
          <>{cell.getValue<string | undefined>()?.abbreviate(50)}</>
        ),
      },
      {
        accessorKey: 'active',
        header: 'Active',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: ActiveStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...ActiveInactivePills(cell.getValue<boolean>())} />
        ),
      },
      {
        accessorKey: 'subformOnly',
        header: 'Subform',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: YesNoStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...YesNoPills(cell.getValue<boolean | undefined>())} />
        ),
      },
      {
        accessorKey: 'extensionOnly',
        header: 'Extension Only',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: YesNoStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...YesNoPills(cell.getValue<boolean | undefined>())} />
        ),
      },
      {
        accessorKey: 'project.name',
        header: 'Project',
      },
      {
        accessorKey: 'category.name',
        header: 'Category',
      },
    ],
    []
  );

  const handleAddNew = () => navigate(PathUtility.FormBuilderCreate());

  const handleNavigateToForm = (id: string | number) =>
    navigate(PathUtility.FormBuilderView(id));

  const tableState = useTableState({
    identifier: 'form-definitions',
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'uniqueIdentifier', desc: false}],
    defaultShowColumnFilters: true,
  });

  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetFormDefinitionListV2Query(
      {
        start: start,
        length: length,
        filters: filters,
        filterFns: filterFns,
        sorting: sorting,
        isRestrictedAccess: false,
      },
      {refetchOnMountOrArgChange: true}
    );

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            onClick={() => handleNavigateToForm(row.original.id)}
          />
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            {hasCreatePermission && (
              <TableToolbar.Action variant="create" onClick={handleAddNew} />
            )}
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return <>{table}</>;
};

export default ListFormBuilderDefinitions;
